'use client';

import { useState, useEffect, useCallback } from 'react';
import { FeedbackType, ImageType } from '@/lib/types';
import api from '@/lib/api';

interface FeedbackButtonsProps {
  imagePath: string;
  imageType: ImageType;
  jobId?: string;
  listId?: number;
  listItemId?: number;
  initialFeedback?: FeedbackType | null;
  onFeedbackChange?: (feedback: FeedbackType | null) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showShortcuts?: boolean; // Show keyboard shortcuts in tooltips (L/D)
}

export default function FeedbackButtons({
  imagePath,
  imageType,
  jobId,
  listId,
  listItemId,
  initialFeedback,
  onFeedbackChange,
  size = 'md',
  className = '',
  showShortcuts = false,
}: FeedbackButtonsProps) {
  const [feedback, setFeedback] = useState<FeedbackType | null>(initialFeedback ?? null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch current feedback when imagePath changes
  useEffect(() => {
    if (initialFeedback !== undefined) {
      // Use provided initial feedback
      setFeedback(initialFeedback);
    } else if (imagePath) {
      // Reset to null immediately, then fetch
      setFeedback(null);
      api.getFeedback(imagePath).then((result) => {
        setFeedback(result?.feedback ?? null);
      });
    }
  }, [imagePath, initialFeedback]);

  const handleFeedback = useCallback(async (newFeedback: FeedbackType) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      if (feedback === newFeedback) {
        // Toggle off - remove feedback
        await api.removeFeedback(imagePath);
        setFeedback(null);
        onFeedbackChange?.(null);
      } else {
        // Set new feedback
        await api.setFeedback({
          image_path: imagePath,
          image_type: imageType,
          feedback: newFeedback,
          job_id: jobId,
          list_id: listId,
          list_item_id: listItemId,
        });
        setFeedback(newFeedback);
        onFeedbackChange?.(newFeedback);
      }
    } catch (error) {
      console.error('Failed to set feedback:', error);
    } finally {
      setIsLoading(false);
    }
  }, [imagePath, imageType, jobId, listId, listItemId, feedback, isLoading, onFeedbackChange]);

  // Size classes
  const sizeClasses = {
    sm: 'w-7 h-7',
    md: 'w-9 h-9',
    lg: 'w-11 h-11',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {/* Like Button */}
      <button
        onClick={() => handleFeedback('like')}
        disabled={isLoading}
        className={`
          ${sizeClasses[size]} flex items-center justify-center rounded-lg transition-all cursor-pointer
          ${feedback === 'like'
            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
            : 'bg-[var(--bg-elevated)]/80 text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-green-400'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        title={feedback === 'like' ? `Remove like${showShortcuts ? ' (G/L)' : ''}` : `Like${showShortcuts ? ' (G/L)' : ''}`}
      >
        <svg
          className={iconSizes[size]}
          fill={feedback === 'like' ? 'currentColor' : 'none'}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
          />
        </svg>
      </button>

      {/* Dislike Button */}
      <button
        onClick={() => handleFeedback('dislike')}
        disabled={isLoading}
        className={`
          ${sizeClasses[size]} flex items-center justify-center rounded-lg transition-all cursor-pointer
          ${feedback === 'dislike'
            ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
            : 'bg-[var(--bg-elevated)]/80 text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-red-400'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        title={feedback === 'dislike' ? `Remove dislike${showShortcuts ? ' (B/D)' : ''}` : `Dislike${showShortcuts ? ' (B/D)' : ''}`}
      >
        <svg
          className={iconSizes[size]}
          fill={feedback === 'dislike' ? 'currentColor' : 'none'}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
          />
        </svg>
      </button>
    </div>
  );
}
